import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Safe back navigation hook that prevents blank pages
 * Falls back to appropriate parent route when history is unavailable
 */
export function useSafeBack(fallback?: string) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Smart fallback based on current route
  const getFallback = () => {
    if (fallback) return fallback;
    
    // Route-specific fallbacks based on logical hierarchy
    const pathMap: Record<string, string> = {
      '/surveys/create': '/surveys',
      '/surveys/edit': '/surveys',
      '/surveys': '/dashboard',
      '/students': '/dashboard',
      '/assessments': '/dashboard',
      '/appointments': '/dashboard',
      '/settings': '/profile',
      '/profile': '/dashboard',
      '/change-password': '/settings',
      '/groups/my-groups': '/dashboard',
      '/groups/join': '/groups/my-groups',
      '/teams': '/dashboard',
      '/reports': '/dashboard',
      '/results': '/dashboard',
      '/provider/results': '/dashboard',
      '/universities': '/dashboard',
      '/user-management': '/dashboard',
    };
    
    // Find the best match for current path
    for (const [pattern, fb] of Object.entries(pathMap)) {
      if (location.pathname.startsWith(pattern)) {
        return fb;
      }
    }
    
    // Default fallback
    return '/dashboard';
  };

  return useCallback(() => {
    // Check if we have actual browser history
    // window.history.length > 1 is unreliable, but combined with document.referrer gives better results
    const hasHistory = window.history.length > 1;
    const hasSameOriginReferrer = document.referrer && document.referrer.includes(window.location.host);
    
    if (hasHistory && hasSameOriginReferrer) {
      // Safe to go back
      navigate(-1);
    } else {
      // Use fallback with replace to avoid creating invalid history
      navigate(getFallback(), { replace: true });
    }
  }, [navigate, location.pathname, fallback]);
}